import { useCallback } from 'react';
import useLogoutFromCognito from '~/client/features/SocialAuth/hooks/useLogoutFromCognito';
import useRouter from '~/client/hooks/router/useRouter';
import useThemeContext from '~/client/hooks/theme/useThemeContext';
import useToastContext from '~/client/layout/DefaultLayout/LayoutContext/Toast/useToastContext';
import useAppContext from '~/shared/context/AppContext/useAppContext';
import useCookie from '~/shared/hooks/useCookie';
import useToken from '~/shared/hooks/useToken';
import removeCustomSymplifyValue from '~/shared/symplify/removeCustomSymplifyValue';
import trackEvent from '~/shared/trackEvent';
import getDefaultTheme from '~/shared/utils/theme/getDefaultTheme';
import { useLogoutUserMutation } from './LogoutUserMutation.generated';
import useLanguage from '../../useLanguage';
import useSite from '../../useSite';

const useLogout = () => {
  const router = useRouter();
  const { siteConfig, site } = useSite();
  const { pathPrefix } = useAppContext();
  const { code } = useLanguage();
  const [, , clearToken] = useToken();
  const [, , clearAuth0Data] = useCookie('auth0.ssodata');
  const [, , clearHideDepositors] = useCookie('hideDepositorsReminder');
  const [logoutUser] = useLogoutUserMutation();
  const { isCognito, logoutFromCognito } = useLogoutFromCognito();
  const { showToast } = useToastContext();
  const { setTheme } = useThemeContext();
  const defaultLang = siteConfig.defaultLang;
  const homeUrl = pathPrefix || '/';

  const logout = useCallback(async () => {
    await logoutUser();
    showToast({ message: 'profile.logged_out' });
    clearToken();
    clearAuth0Data();
    clearHideDepositors();

    trackEvent({ loginType: 'direct' }, 'logoutSuccess');
    removeCustomSymplifyValue();

    setTheme(getDefaultTheme(site));
    if (window.gib) {
      window.gib.setAuthStatus(window.gib.IS_NOT_AUTHORIZED);
      window.gib.setSessionID('');
      window.gib.setIdentity('');
    }
    if (isCognito) {
      logoutFromCognito();
      return;
    }
    const redirectUrl = code !== defaultLang ? `${homeUrl}${code}` : homeUrl;
    if (router.route === homeUrl) {
      location.replace(redirectUrl);
      return;
    }
    router.push(homeUrl, undefined);
  }, [
    logoutUser,
    clearHideDepositors,
    showToast,
    clearToken,
    clearAuth0Data,
    setTheme,
    site,
    isCognito,
    code,
    defaultLang,
    homeUrl,
    router,
    logoutFromCognito,
  ]);

  return logout;
};

export default useLogout;
